<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { OperationModelType } from "~/core/models/OperationModelType.enum";
import { Filters } from "~/core/models/filters";
import { useDtwinOperationModels } from "~/modules/dtwin/composables/useDtwinOperationManager";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import type { DtwinOperationCertificateInputParameters } from "~/modules/dtwin/models/DtwinOperationCertificateInputParameters.interface";
import type { DynamicUIDetail } from "~/modules/dtwin/models/DynamicUIDetail.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcPlus from "~icons/lxc/plus";

const props = defineProps<{
  organizationCode: number;
  dtwin?: Dtwin;
  dtwinModel?: DtwinModel;
  deviceModelUiConfig?: Record<string, Record<string, DynamicUIDetail[]>>;
}>();

const { t } = useI18n();

const { can } = useAcl();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  error: errorOperationModels,
  fetchData: fetchOperationModels,
  results: resultsOperationModels,
  setFilter: setFilterOperationModels,
} = useDtwinOperationModels({
  organizationCode: currentOrganization.value?.code,
  searchMode: SearchMode.FILTER_SEARCH,
});

const operationListRef = ref();

const isLoadingDtwinOperationModel = ref(false);
const errorDtwinOperationModel = ref();
const dtwinOperationModel: Ref<OperationModel | undefined> = ref();

const operationFormShown = ref(false);
const operationFormModel: Ref<
  DtwinOperation<DtwinOperationCertificateInputParameters> | undefined
> = ref();

const canUpdateOperations: ComputedRef<boolean> = computed(() =>
  can(ACLRoles.CAN_MANAGE_CERTIFICATE_OPERATION),
);

async function getOperationModel() {
  if (!dtwinOperationModel.value) {
    setFilterOperationModels(
      Filters.DTWIN_OPERATION_MODEL_TYPE,
      OperationModelType.CERT_RENEW,
    );

    if (props.dtwinModel) {
      setFilterOperationModels(Filters.DTWIN_OPERATION_MODEL_DEVICE_TYPE, [
        props.dtwinModel.name,
      ]);
    }
    await fetchOperationModels();

    if (errorOperationModels.value) {
      errorDtwinOperationModel.value = errorOperationModels.value;
    } else {
      const models = resultsOperationModels.value?.results;
      dtwinOperationModel.value = models?.length ? models[0] : undefined;
    }
  }
}

function showOperationForm(
  show: boolean,
  operation?: DtwinOperation<DtwinOperationCertificateInputParameters>,
) {
  operationFormModel.value = operation;
  operationFormShown.value = show;
}

function reloadOperationList() {
  operationListRef.value?.reload();
}

onMounted(getOperationModel);
</script>

<template>
  <container-component
    :px="0"
    :py="0"
    :is-loading="isLoadingDtwinOperationModel"
    :error="errorDtwinOperationModel"
  >
    <block-container
      v-if="dtwin && deviceModelUiConfig"
      :title="t('dtwins.form.certificate.information.title')"
      title-top
    >
      <template #left>
        <template
          v-for="item in deviceModelUiConfig.information.left"
          :key="item.id"
        >
          <dtwin-form-field :item="item" :dtwin="dtwin" />
        </template>
      </template>
      <template #right>
        <dtwin-form-field
          v-for="item in deviceModelUiConfig.information.right"
          :key="item.id"
          :item="item"
          :dtwin="dtwin"
        />
      </template>
    </block-container>

    <block-container
      v-if="canUpdateOperations && dtwin && dtwinModel && dtwinOperationModel"
      title-top
      with-margin-bottom
    >
      <lxc-button
        type="primary"
        html-type="button"
        :icon="ILxcPlus"
        :title="t('operation.certificate.update.label')"
        :disabled="dtwin.lifeCycleState === DtwinLifeCycleState.DISABLED"
        @click.prevent="showOperationForm(true)"
        >{{ t("operation.certificate.update.label") }}</lxc-button
      >
    </block-container>

    <block-container
      v-if="
        can(ACLRoles.CAN_MANAGE_CERTIFICATE_OPERATION) &&
        dtwin &&
        dtwinModel &&
        dtwinOperationModel
      "
      :title="t('section.operations.title')"
      title-top
    >
      <dtwin-form-certificate-operation-list
        ref="operationListRef"
        :organization-code="organizationCode"
        :dtwin="dtwin"
        :dtwin-model="dtwinModel"
        :dtwin-operation-model="dtwinOperationModel"
        @edit="
          (
            operation: DtwinOperation<DtwinOperationCertificateInputParameters>,
          ) => showOperationForm(true, operation)
        "
      />
    </block-container>

    <dtwin-form-certificate-operation-form
      v-if="canUpdateOperations"
      v-model:show="operationFormShown"
      :dtwin="dtwin"
      :dtwin-operation-model="dtwinOperationModel"
      :operation="operationFormModel"
      @change="reloadOperationList"
    />
  </container-component>
</template>
